import React, { useEffect, useState } from 'react';
import { FiBell, FiSliders } from 'react-icons/fi';
import api from '../../../api/api';
import { NotificationPreferences as Prefs, NotificationEvents } from '../../../model/NotificationPreferences';
import { to } from '../../../util/util';
import { toast } from '../Toast';
import Toggle from '../Toggle';
import AccountNav from './AccountNav';

type Channel = 'instant' | 'digest' | 'inApp';
const CHANNELS: { key: Channel; label: string }[] = [
    { key: 'instant', label: 'Instant email' },
    { key: 'digest', label: 'Digest' },
    { key: 'inApp', label: 'In app' },
];

interface EventRow {
    // stored rows are keyed by NotificationEvents; fixed rows aren't stored
    key: keyof NotificationEvents | 'approval' | 'security';
    label: string;
    note?: string;
    // always on and not stored
    fixed?: Channel[];
}

const EVENTS: EventRow[] = [
    { key: 'approval', label: 'Announcement needs approval', note: 'Always on for approvers', fixed: ['instant', 'digest', 'inApp'] },
    { key: 'question', label: 'New investor question' },
    { key: 'questionWaiting', label: 'Question waiting more than 48 hours' },
    { key: 'registryReport', label: 'Registry report ready' },
    { key: 'largeMovement', label: 'Large holder movement (over 1M shares)' },
    { key: 'campaignReport', label: 'Campaign report ready (7 days)' },
    { key: 'hubSignUp', label: 'New Website Hub sign‑up' },
    { key: 'security', label: 'Security alerts (new sign‑in, password change)', note: 'Always on', fixed: ['instant'] },
];

const errorText = (err: any, fallback: string) => typeof err?.response?.data === 'string' ? err.response.data : fallback;

const NotificationPreferences: React.FC = () => {
    const [prefs, setPrefs] = useState<Prefs>();
    const [savedPrefs, setSavedPrefs] = useState<Prefs>();
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const dirty = !!prefs && JSON.stringify(prefs) !== JSON.stringify(savedPrefs);

    useEffect(() => {
        api().getNotificationPreferences().then(r => {
            setPrefs(r.preferences);
            setSavedPrefs(r.preferences);
        }).catch(() => setLoadError('Could not load your notification preferences'));
    }, []);

    const set = (patch: Partial<Prefs>) => setPrefs(p => p && { ...p, ...patch });
    const setCell = (event: keyof NotificationEvents, channel: Channel, value: boolean) =>
        setPrefs(p => p && { ...p, events: { ...p.events, [event]: { ...p.events[event], [channel]: value } } });

    const save = async () => {
        if (!prefs) return;
        setSaving(true);
        const [err, result] = await to(api().saveNotificationPreferences(prefs));
        setSaving(false);
        if (err) {
            toast.error(errorText(err, 'Could not save your notification preferences'));
            return;
        }
        setPrefs(result.preferences);
        setSavedPrefs(result.preferences);
        toast.success('Notification preferences saved');
    };

    const saveButton = <button type="button" className="btn-primary" onClick={save} disabled={!dirty || saving}>Save Changes</button>;

    return <>
        <div className="page-header">
            <div>
                <h1>Notification Preferences</h1>
                <p>Choose what you hear about, and how. Approvals and security alerts are always on.</p>
            </div>
            <div className="action-top">{saveButton}</div>
        </div>

        <div className="account-row">
            <AccountNav />

            <div className="account-main">
                {loadError && <p className="error">{loadError}</p>}
                {prefs && <>
                    <section className="card">
                        <div className="section-title"><span className="icon-tile"><FiBell /></span><h2>How You'd Like to Be Notified</h2></div>
                        <div className="setting-row">
                            <div className="text">
                                <div className="title">Morning email digest</div>
                                <div className="hint">One email at 8:00 am AWST with everything from the day before</div>
                            </div>
                            <Toggle label="Morning email digest" checked={prefs.digest} onChange={v => set({ digest: v })} />
                        </div>
                        <div className="setting-row">
                            <div className="text">
                                <div className="title">SMS for urgent items</div>
                                <div className="hint">Approvals waiting more than 2 hours on a trading day</div>
                            </div>
                            <Toggle label="SMS for urgent items" checked={prefs.sms} onChange={v => set({ sms: v })} />
                        </div>
                    </section>

                    <section className="card">
                        <div className="section-title"><span className="icon-tile"><FiSliders /></span><h2>What to Notify Me About</h2></div>
                        <div className="matrix">
                            <div className="matrix-head">
                                <span />
                                {CHANNELS.map(c => <span key={c.key} className="cell">{c.label}</span>)}
                            </div>
                            {EVENTS.map(e => <div key={e.key} className="matrix-row">
                                <div className="text">
                                    <div className="title">{e.label}</div>
                                    {e.note && <div className="note">{e.note}</div>}
                                </div>
                                {CHANNELS.map(c => {
                                    if (e.fixed) {
                                        return <span key={c.key} className="cell">
                                            {e.fixed.includes(c.key) && <Toggle label={`${e.label}: ${c.label}`} checked locked faded />}
                                        </span>;
                                    }
                                    const key = e.key as keyof NotificationEvents;
                                    return <span key={c.key} className="cell">
                                        <Toggle label={`${e.label}: ${c.label}`} checked={prefs.events[key][c.key]} onChange={v => setCell(key, c.key, v)} />
                                    </span>;
                                })}
                            </div>)}
                        </div>
                    </section>

                    {/* mobile: Save moves from the header to the end of the page (Account.scss .action-top / .action-bottom) */}
                    <div className="action-bottom">{saveButton}</div>
                </>}
            </div>
        </div>
    </>;
};

export default NotificationPreferences;
